import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePage } from '@inertiajs/react';
import {
    isTrackablePath,
    normalizePath,
    resolvePageTitle,
} from '../lib/menuPath';
import type { NexusNavItem, PageProps } from '../types';

export type RecentPage = {
    id: string;
    title: string;
    href: string;
    pinned: boolean;
    visitedAt: number;
};

const MAX_UNPINNED = 10;
const STORAGE_PREFIX = 'nexus-recent-pages';

function storageKey(userId: number | string | null | undefined): string {
    return `${STORAGE_PREFIX}:${userId ?? 'guest'}`;
}

function readStored(key: string): RecentPage[] {
    if (typeof window === 'undefined') {
        return [];
    }

    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .filter(
                (item): item is RecentPage =>
                    !!item &&
                    typeof item === 'object' &&
                    typeof (item as RecentPage).id === 'string' &&
                    typeof (item as RecentPage).href === 'string' &&
                    typeof (item as RecentPage).title === 'string',
            )
            .map((item) => ({
                id: item.id,
                title: item.title,
                href: item.href,
                pinned: Boolean(item.pinned),
                visitedAt: Number(item.visitedAt) || 0,
            }));
    } catch {
        return [];
    }
}

function writeStored(key: string, pages: RecentPage[]): void {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(key, JSON.stringify(pages));
    } catch {
        // quota / private mode
    }
}

function sortPages(pages: RecentPage[]): RecentPage[] {
    return [...pages].sort((a, b) => {
        if (a.pinned !== b.pinned) {
            return a.pinned ? -1 : 1;
        }
        return b.visitedAt - a.visitedAt;
    });
}

function applyLruLimit(pages: RecentPage[]): RecentPage[] {
    const pinned = pages.filter((p) => p.pinned);
    const unpinned = pages
        .filter((p) => !p.pinned)
        .sort((a, b) => b.visitedAt - a.visitedAt)
        .slice(0, MAX_UNPINNED);

    return sortPages([...pinned, ...unpinned]);
}

type UseRecentPagesOptions = {
    pageTitle?: string | null;
};

export function useRecentPages({ pageTitle }: UseRecentPagesOptions = {}) {
    const { url, props } = usePage<PageProps>();
    const menu = (props.menu ?? []) as NexusNavItem[];
    const userId = props.auth?.user?.id ?? null;
    const key = useMemo(() => storageKey(userId), [userId]);

    const [pages, setPages] = useState<RecentPage[]>(() => readStored(key));
    const lastTrackedPath = useRef<string | null>(null);

    const menuRef = useRef(menu);
    menuRef.current = menu;
    const pageTitleRef = useRef(pageTitle);
    pageTitleRef.current = pageTitle;

    useEffect(() => {
        setPages(readStored(key));
        lastTrackedPath.current = null;
    }, [key]);

    const currentPath = normalizePath(url);

    useEffect(() => {
        if (!isTrackablePath(currentPath)) {
            return;
        }

        const title = resolvePageTitle({
            menu: menuRef.current,
            path: currentPath,
            pageTitle: pageTitleRef.current,
            documentTitle: typeof document !== 'undefined' ? document.title : null,
        });

        const isNewVisit = lastTrackedPath.current !== currentPath;
        lastTrackedPath.current = currentPath;

        setPages((prev) => {
            const existing = prev.find((p) => p.id === currentPath);
            const nextItem: RecentPage = {
                id: currentPath,
                title,
                href: currentPath,
                pinned: existing?.pinned ?? false,
                visitedAt: isNewVisit ? Date.now() : (existing?.visitedAt ?? Date.now()),
            };

            // Mesma URL: só atualiza título se mudou
            if (!isNewVisit && existing && existing.title === title) {
                return prev;
            }

            const without = prev.filter((p) => p.id !== currentPath);
            const limited = applyLruLimit([nextItem, ...without]);
            writeStored(key, limited);
            return limited;
        });
    }, [currentPath, key, pageTitle]);

    const togglePin = useCallback(
        (id: string) => {
            setPages((prev) => {
                const next = applyLruLimit(
                    prev.map((p) =>
                        p.id === id ? { ...p, pinned: !p.pinned } : p,
                    ),
                );
                writeStored(key, next);
                return next;
            });
        },
        [key],
    );

    const removePage = useCallback(
        (id: string) => {
            setPages((prev) => {
                const target = prev.find((p) => p.id === id);
                if (target?.pinned) {
                    return prev;
                }
                const next = applyLruLimit(prev.filter((p) => p.id !== id));
                writeStored(key, next);
                return next;
            });
        },
        [key],
    );

    return {
        pages,
        currentPath,
        togglePin,
        removePage,
    };
}
